import React, { useState, useEffect } from 'react';

export default function IngredientInput({ 
    ingredient, 
    index, 
    handleIngredientChange, 
    removeIngredient 
}) {
    const baseUnits = ['kg', 'g', 'l', 'ml', 'unit', 'R/kg', 'whole'];
    const [showSpecialOptions, setShowSpecialOptions] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);

    // Initialize showSpecialOptions based on whether there's an active special
    useEffect(() => {
        if (ingredient.special?.active) {
            setShowSpecialOptions(true);
        }
        if (ingredient.imageUrl) {
            setImagePreview(ingredient.imageUrl);
        }
    }, [ingredient.special?.active, ingredient.imageUrl]);

    // Handle base unit change to automatically set equivalent unit
    const handleBaseUnitChange = (e) => {
        const newBaseUnit = e.target.value;
        handleIngredientChange(index, 'baseUnit', newBaseUnit);
        
        // If it's not R/kg, automatically set the equivalent unit to match base unit
        if (newBaseUnit && newBaseUnit !== 'R/kg') {
            handleIngredientChange(index, 'unitEquivalent.unit', newBaseUnit);
        }
    };

    const handleSpecialTypeChange = (e) => {
        const specialType = e.target.value;
        
        // Reset all special fields when type changes
        const resetSpecial = {
            type: specialType,
            buyQuantity: '',
            getQuantity: '',
            specialPrice: '',
            savingsAmount: '',
            savingsPercentage: '',
            active: specialType !== ''
        };

        // Update each field individually
        Object.entries(resetSpecial).forEach(([key, value]) => {
            handleIngredientChange(index, `special.${key}`, value);
        });
    };

    const handleMultiBuyChange = (field, value) => {
        handleIngredientChange(index, `special.${field}`, value);
        
        // Calculate savings if both buyQuantity and specialPrice are set
        if (ingredient.special?.buyQuantity && ingredient.special?.specialPrice) {
            const originalCost = ingredient.special.buyQuantity * ingredient.costPerUnit;
            const specialPrice = parseFloat(ingredient.special.specialPrice);
            const savings = originalCost - specialPrice;
            const savingsPercent = ((savings / originalCost) * 100).toFixed(1);
            
            handleIngredientChange(index, 'special.savingsAmount', savings);
            handleIngredientChange(index, 'special.savingsPercentage', savingsPercent);
        }
    };

    const handleSpecialToggle = (e) => {
        const isChecked = e.target.checked;
        setShowSpecialOptions(isChecked);
        
        if (!isChecked) {
            // Reset special fields when unchecking
            handleIngredientChange(index, 'special', {
                type: '',
                buyQuantity: '',
                getQuantity: '',
                specialPrice: '',
                savingsAmount: '',
                savingsPercentage: '',
                active: false
            });
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Create a preview URL
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
            
            // Pass the file to parent component
            handleIngredientChange(index, 'image', file);
        }
    };

    return (
        <div className="ingredient-input">
            {/* Image upload section */}
            <div className="image-upload-section">
                <label>Ingredient Image:</label>
                <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ marginBottom: '10px' }}
                />
                {imagePreview && (
                    <div className="image-preview">
                        <img 
                            src={imagePreview} 
                            alt="Ingredient preview" 
                            style={{
                                maxWidth: '100px',
                                maxHeight: '100px',
                                objectFit: 'cover',
                                marginBottom: '10px'
                            }}
                        />
                    </div>
                )}
            </div>

            <input
                type="text"
                placeholder="Ingredient name"
                value={ingredient.name}
                onChange={(e) => handleIngredientChange(index, 'name', e.target.value)}
            />
            <input
                type="number"
                step="0.01"
                min="0"
                placeholder="Cost per unit"
                value={ingredient.costPerUnit}
                onChange={(e) => handleIngredientChange(index, 'costPerUnit', e.target.value)}
            />
            <select
                value={ingredient.baseUnit}
                onChange={handleBaseUnitChange}
            >
                <option value="">Select base unit</option>
                {baseUnits.map(unit => (
                    <option key={unit} value={unit}>{unit}</option>
                ))}
            </select>
            
            {/* Only show unit equivalent amount if baseUnit is not R/kg */}
            {ingredient.baseUnit && ingredient.baseUnit !== 'R/kg' && (
                <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Unit equivalent amount"
                    value={ingredient.unitEquivalent?.amount}
                    onChange={(e) => handleIngredientChange(index, 'unitEquivalent.amount', e.target.value)}
                />
            )}
            
            <input
                type="text"
                placeholder="Brand"
                value={ingredient.brand}
                onChange={(e) => handleIngredientChange(index, 'brand', e.target.value)}
            />

            {/* Special checkbox */}
            <div className="special-section">
                <label>
                    <input
                        type="checkbox"
                        checked={showSpecialOptions}
                        onChange={handleSpecialToggle}
                    />
                    Has Special Offer
                </label>

                {showSpecialOptions && (
                    <div className="special-options">
                        <select
                            value={ingredient.special?.type || ''}
                            onChange={(e) => handleSpecialTypeChange(e)}
                        >
                            <option value="">Select Special Type</option>
                            <option value="MULTI_BUY">Buy X for Price Y</option>
                            <option value="PERCENTAGE_OFF">Percentage Off</option>
                            <option value="FIXED_PRICE">Fixed Price Special</option>
                            <option value="BUY_X_GET_Y">Buy X Get Y</option>
                        </select>

                        {ingredient.special?.type === 'MULTI_BUY' && (
                            <>
                                <input
                                    type="number"
                                    placeholder="Buy Quantity"
                                    value={ingredient.special?.buyQuantity || ''}
                                    onChange={(e) => handleIngredientChange(index, 'special.buyQuantity', e.target.value)}
                                />
                                <select
                                    value={ingredient.special?.discountType || 'FIXED'}
                                    onChange={(e) => handleIngredientChange(index, 'special.discountType', e.target.value)}
                                >
                                    <option value="FIXED">Fixed Price</option>
                                    <option value="PERCENTAGE">Percentage Off</option>
                                </select>
                                {ingredient.special?.discountType === 'FIXED' ? (
                                    <input
                                        type="number"
                                        step="0.01"
                                        placeholder="Special Price"
                                        value={ingredient.special?.specialPrice || ''}
                                        onChange={(e) => handleIngredientChange(index, 'special.specialPrice', e.target.value)}
                                    />
                                ) : (
                                    <input
                                        type="number"
                                        placeholder="Discount Percentage"
                                        value={ingredient.special?.discountPercent || ''}
                                        onChange={(e) => handleIngredientChange(index, 'special.discountPercent', e.target.value)}
                                    />
                                )}
                            </>
                        )}

                        {ingredient.special?.type === 'PERCENTAGE_OFF' && (
                            <input
                                type="number"
                                placeholder="Savings Percentage"
                                value={ingredient.special?.savingsPercentage || ''}
                                onChange={(e) => handleIngredientChange(index, 'special.savingsPercentage', e.target.value)}
                            />
                        )}

                        {ingredient.special?.type === 'FIXED_PRICE' && (
                            <input
                                type="number"
                                step="0.01"
                                placeholder="Special Price"
                                value={ingredient.special?.specialPrice || ''}
                                onChange={(e) => handleIngredientChange(index, 'special.specialPrice', e.target.value)}
                            />
                        )}

                        {ingredient.special?.type === 'BUY_X_GET_Y' && (
                            <>
                                <input
                                    type="number"
                                    placeholder="Buy Quantity"
                                    value={ingredient.special?.buyQuantity || ''}
                                    onChange={(e) => handleIngredientChange(index, 'special.buyQuantity', e.target.value)}
                                />
                                <input
                                    type="number"
                                    placeholder="Get Quantity"
                                    value={ingredient.special?.getQuantity || ''}
                                    onChange={(e) => handleIngredientChange(index, 'special.getQuantity', e.target.value)}
                                />
                            </>
                        )}
                    </div>
                )}
            </div>
            
            <button type="button" onClick={() => removeIngredient(index)}>
                Remove
            </button>

            <style>
            {`
                .image-upload-section {
                    margin-bottom: 15px;
                }
                
                .image-preview {
                    margin-top: 10px;
                    border: 1px solid #ddd;
                    padding: 5px;
                    border-radius: 4px;
                    display: inline-block;
                }
            `}
            </style>
        </div>
    );
}
